import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Wallet, DollarSign, ArrowRight, Loader2, ExternalLink,
  CheckCircle2, AlertCircle, Clock, History,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const statusBadge = (status) => {
  switch (status) {
    case "verified":
      return <Badge className="bg-green-600 text-white" data-testid="connect-status-verified"><CheckCircle2 className="w-3 h-3 mr-1" />Verified</Badge>;
    case "pending":
      return <Badge className="bg-yellow-500 text-white" data-testid="connect-status-pending"><Clock className="w-3 h-3 mr-1" />Pending Review</Badge>;
    case "incomplete":
      return <Badge className="bg-orange-500 text-white" data-testid="connect-status-incomplete"><AlertCircle className="w-3 h-3 mr-1" />Onboarding Incomplete</Badge>;
    default:
      return <Badge variant="outline" data-testid="connect-status-none">Not Connected</Badge>;
  }
};

export const DriverPayoutsTab = ({ driverToken }) => {
  const [status, setStatus] = useState(null);
  const [earnings, setEarnings] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cashingOut, setCashingOut] = useState(false);
  const [onboarding, setOnboarding] = useState(false);
  const [cashapp, setCashapp] = useState("");
  const [zelle, setZelle] = useState("");
  const [savingTags, setSavingTags] = useState(false);

  const headers = { Authorization: `Bearer ${driverToken}` };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, e, h] = await Promise.all([
        axios.get(`${API}/api/driver/payouts/status`, { headers }),
        axios.get(`${API}/api/driver/payouts/earnings`, { headers }),
        axios.get(`${API}/api/driver/payouts/history`, { headers }),
      ]);
      setStatus(s.data);
      setEarnings(e.data.earnings || []);
      setHistory(h.data.payouts || []);
      setCashapp(s.data.cashapp_tag || "");
      setZelle(s.data.zelle_email || "");
    } catch (err) {
      toast.error("Failed to load payout data");
    } finally {
      setLoading(false);
    }
  }, [driverToken]); // eslint-disable-line

  useEffect(() => {
    if (driverToken) fetchAll();
    // Detect ?stripe=onboarded from Stripe return URL and refresh
    const params = new URLSearchParams(window.location.search);
    if (params.get("stripe") === "onboarded") {
      setTimeout(fetchAll, 1500);
      toast.success("Returned from Stripe — refreshing status…");
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, "", cleanUrl);
    }
  }, [driverToken]); // eslint-disable-line

  const startOnboarding = async () => {
    setOnboarding(true);
    try {
      const { data } = await axios.post(`${API}/api/driver/payouts/onboard`, {}, { headers });
      window.location.href = data.onboarding_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to start onboarding");
      setOnboarding(false);
    }
  };

  const cashOut = async () => {
    if (!status?.accrued_balance || status.accrued_balance < 1) {
      toast.error("No earnings available to cash out");
      return;
    }
    setCashingOut(true);
    try {
      const { data } = await axios.post(`${API}/api/driver/payouts/cashout`, {}, { headers });
      toast.success(`$${data.payout.amount.toFixed(2)} sent to your bank!`);
      await fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Cashout failed");
    } finally {
      setCashingOut(false);
    }
  };

  const saveTags = async () => {
    setSavingTags(true);
    try {
      await axios.put(`${API}/api/driver/payouts/settings`,
        { cashapp_tag: cashapp || null, zelle_email: zelle || null },
        { headers });
      toast.success("Payout settings saved");
      await fetchAll();
    } catch {
      toast.error("Failed to save");
    } finally {
      setSavingTags(false);
    }
  };

  if (loading && !status) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Stripe Connect Card */}
      <Card data-testid="stripe-connect-card">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center justify-between text-lg">
            <span className="flex items-center gap-2">
              <Wallet className="w-5 h-5 text-blue-600" />
              Stripe Direct Deposit
            </span>
            {statusBadge(status?.onboarding_status)}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {status?.onboarding_status === "verified" ? (
            <p className="text-sm text-muted-foreground">
              Your bank is verified. Cash out anytime — funds arrive in 1-2 business days.
            </p>
          ) : (
            <p className="text-sm text-muted-foreground">
              Connect your bank to receive trip earnings directly. Stripe handles ID verification, tax forms, and deposits.
            </p>
          )}
          {status?.requirements_due?.length > 0 && (
            <div className="text-xs bg-orange-50 border border-orange-200 rounded p-2">
              <p className="font-semibold text-orange-800">Stripe needs:</p>
              <ul className="list-disc list-inside text-orange-700">
                {status.requirements_due.slice(0, 4).map(r => <li key={r}>{r.replaceAll("_", " ")}</li>)}
              </ul>
            </div>
          )}
          <Button
            onClick={startOnboarding}
            disabled={onboarding}
            className="w-full bg-[#635BFF] hover:bg-[#5247e0] text-white"
            data-testid="connect-stripe-btn"
          >
            {onboarding ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ExternalLink className="w-4 h-4 mr-2" />}
            {status?.stripe_account_id ? "Continue with Stripe" : "Connect Stripe Account"}
          </Button>
        </CardContent>
      </Card>

      {/* Balance + Cash Out */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-green-600" />
            Available to Cash Out
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="text-center py-4 bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg border border-green-200">
            <p className="text-4xl font-bold text-green-700" data-testid="accrued-balance">
              ${(status?.accrued_balance || 0).toFixed(2)}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Lifetime payouts: ${(status?.lifetime_payouts || 0).toFixed(2)}
            </p>
          </div>
          <Button
            onClick={cashOut}
            disabled={cashingOut || !status?.payouts_enabled || (status?.accrued_balance || 0) < 1}
            className="w-full bg-green-600 hover:bg-green-700"
            data-testid="cashout-btn"
          >
            {cashingOut ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ArrowRight className="w-4 h-4 mr-2" />}
            Cash Out ${(status?.accrued_balance || 0).toFixed(2)}
          </Button>
          {!status?.payouts_enabled && status?.stripe_account_id && (
            <p className="text-xs text-yellow-700 text-center">
              Finish Stripe onboarding above to enable cashouts
            </p>
          )}
          <div className="text-xs text-muted-foreground border-t pt-2">
            <p>
              Your share: <span className="font-semibold">
                {status?.payout_share_fixed
                  ? `$${status.payout_share_fixed} flat per trip`
                  : `${status?.payout_share_pct || 80}% of each trip`}
              </span> + 100% of tips
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Per-trip earnings */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Recent Earnings</CardTitle>
        </CardHeader>
        <CardContent>
          {earnings.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              No earnings yet. Complete a trip to start earning.
            </p>
          ) : (
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {earnings.slice(0, 30).map(e => (
                <div
                  key={e.id}
                  className={`flex justify-between items-center text-sm border-l-4 pl-3 py-1.5 ${e.paid_out ? 'border-gray-300 bg-gray-50' : 'border-green-500 bg-green-50'}`}
                  data-testid={`earning-${e.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <p className="font-medium truncate">{e.passenger_name || '—'}</p>
                    <p className="text-xs text-muted-foreground">
                      Trip ${e.trip_price?.toFixed(2)} • {e.paid_out ? 'paid out' : 'pending'}
                    </p>
                  </div>
                  <p className="font-bold text-green-700 whitespace-nowrap">
                    +${e.driver_share?.toFixed(2)}
                  </p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Payout history */}
      {history.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <History className="w-4 h-4" /> Cashout History
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-1.5">
              {history.slice(0, 20).map(p => (
                <div key={p.id} className="flex justify-between text-sm border-b pb-1.5" data-testid={`payout-${p.id}`}>
                  <span className="text-xs text-muted-foreground">
                    {new Date(p.created_at).toLocaleDateString()}
                  </span>
                  <span className="font-semibold">${p.amount?.toFixed(2)}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Driver-managed CashApp/Zelle fallback */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Alternate Payout Methods</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label className="text-xs">CashApp Tag</Label>
            <Input
              value={cashapp}
              onChange={(e) => setCashapp(e.target.value)}
              placeholder="$YourCashAppTag"
              data-testid="cashapp-input"
            />
          </div>
          <div>
            <Label className="text-xs">Zelle Email</Label>
            <Input
              type="email"
              value={zelle}
              onChange={(e) => setZelle(e.target.value)}
              placeholder="you@email.com"
              data-testid="zelle-input"
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={saveTags}
            disabled={savingTags}
            className="w-full"
            data-testid="save-payout-settings-btn"
          >
            {savingTags && <Loader2 className="w-3 h-3 animate-spin mr-2" />}
            Save Payout Methods
          </Button>
          <p className="text-xs text-muted-foreground">
            Customers will see these on the in-trip "Take Payment" page if they prefer to pay you directly.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};
